import React, { useEffect, useState, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useHotel } from "../../contexts/HotelContext";
import { usePmsConfiguration } from "../../contexts/PmsConfigurationContext";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useUnifiedNotificationStore } from "../../stores/unifiedNotificationStore";
import { getNotificationCount } from "../../config/adminNavigationConfig";
import {
  createNavigationGroupsFromRoutes,
  DEFAULT_EXPANDED_GROUPS,
} from "../../config/routes/navigationGroups";
import CollapsibleNavGroup from "./CollapsibleNavGroup";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { X, Settings, Search } from "lucide-react";
import { Input } from "../ui/input";

interface SidebarProps {
  isOpen: boolean;
  userPermissions: string[];
  onClose: () => void;
  limitToBilling?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  userPermissions,
  onClose,
  limitToBilling,
}) => {
  const { t } = useTranslation();
  // Get all notification counts from unified store
  const {
    pendingOrdersCount,
    pendingLateCheckoutsCount,
    pendingServiceRequestsCount,
    unreadChatsCount,
    pendingTransferRequestsCount,
    pendingActivityBookingsCount,
    todayCheckinsCount,
    todayCheckinsBaseline,
    setTodayCheckinsBaseline,
  } = useUnifiedNotificationStore();

  const { myHotelInfo } = useHotel();
  const { isPmsConfigured, isLoading: isPmsLoading } = usePmsConfiguration();
  const { digitalLocks } = useFeatureFlags();

  // PMS-specific routes to hide when PMS is not configured
  const pmsRoutes = ['/admin/pms-outbox'];
  // Lock routes to hide when digital locks feature is disabled
  const lockRoutes = ['/admin/lock-connections'];

  // State for expanded groups
  const [expandedGroups, setExpandedGroups] = useState<string[]>(() => {
    const saved = localStorage.getItem("sidebarExpandedGroups");
    return saved ? JSON.parse(saved) : DEFAULT_EXPANDED_GROUPS;
  });

  const [searchQuery, setSearchQuery] = useState("");

  // Create navigation groups from route configuration
  let navigationGroups = createNavigationGroupsFromRoutes(userPermissions);
  if (limitToBilling) {
    navigationGroups = navigationGroups.filter((g) => g.id === "billing")
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => item.to !== "/admin/revenue"),
      }));
  }

  // Filter out PMS-related navigation items when PMS is not configured
  // Only apply filter after PMS config is loaded to avoid flash
  if (!isPmsLoading && !isPmsConfigured) {
    navigationGroups = navigationGroups.map((group) => ({
      ...group,
      items: group.items.filter((item) => !pmsRoutes.includes(item.to)),
    })).filter((group) => group.items.length > 0);
  }

  // Filter out lock-related navigation items when digital locks feature is disabled
  if (!digitalLocks) {
    navigationGroups = navigationGroups.map((group) => ({
      ...group,
      items: group.items.filter((item) => !lockRoutes.includes(item.to)),
    })).filter((group) => group.items.length > 0);
  }

  // Filter groups based on search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return navigationGroups;

    const lowerQuery = searchQuery.toLowerCase();
    return navigationGroups
      .map((group) => {
        // Check if group title matches
        const groupTitle = t(group.titleKey, group.defaultTitle).toLowerCase();
        const groupMatches = groupTitle.includes(lowerQuery);

        // Filter items
        const filteredItems = group.items.filter((item) => {
          const itemText = t(item.textKey, item.defaultText).toLowerCase();
          return itemText.includes(lowerQuery);
        });

        if (groupMatches || filteredItems.length > 0) {
          return {
            ...group,
            items: groupMatches ? group.items : filteredItems, // If group matches, show all. If not, show matching items.
          };
        }
        return null;
      })
      .filter(Boolean) as typeof navigationGroups;
  }, [searchQuery, navigationGroups, t]);

  // Detect if screen is small height
  const [isCompact, setIsCompact] = useState(window.innerHeight < 800);

  useEffect(() => {
    const handleResize = () => {
      setIsCompact(window.innerHeight < 800);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load baseline for check-ins badge per hotel
  useEffect(() => {
    const hotelId = myHotelInfo?.hotelId;
    if (!hotelId) return;
    try {
      const stored = localStorage.getItem(`checkinsBaseline:${hotelId}`);
      const val = stored ? parseInt(stored, 10) : 0;
      if (!isNaN(val) && val !== todayCheckinsBaseline)
        setTodayCheckinsBaseline(val);
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myHotelInfo?.hotelId]);

  // Consolidate notification sources (mapping to match existing navigation config)
  const notificationSources = {
    newOrderCount: pendingOrdersCount,
    newLateCheckoutCount: pendingLateCheckoutsCount,
    newServiceRequestCount: pendingServiceRequestsCount,
    unreadChatCount: unreadChatsCount,
    pendingTransferRequestCount: pendingTransferRequestsCount,
    pendingActivityBookingsCount,
    todayCheckinsCount: Math.max(0, todayCheckinsCount - todayCheckinsBaseline),
    // Unified requests count: sum of all request types that the unified page consolidates
    unifiedRequestsCount:
      pendingOrdersCount +
      pendingLateCheckoutsCount +
      pendingServiceRequestsCount +
      pendingTransferRequestsCount,
  };

  // No longer need to manually fetch counts - the UnifiedNotificationProvider handles this

  const handleLinkClick = () => {
    if (isOpen) {
      onClose();
    }
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const newExpanded = prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId];
      localStorage.setItem(
        "sidebarExpandedGroups",
        JSON.stringify(newExpanded),
      );
      return newExpanded;
    });
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/80 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "w-64 sidebar-gradient transition-transform duration-200 ease-in-out",
          "lg:static lg:translate-x-0",
          "fixed inset-y-0 left-0 z-50 lg:z-auto",
          isOpen ? "translate-x-0" : "-translate-x-full",
          "shrink-0",
          isCompact && "sidebar-compact",
        )}
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex h-16 items-center justify-between px-4 sidebar-header">
            <NavLink
              to="/admin/dashboard"
              className="flex items-center gap-2"
              onClick={handleLinkClick}
            >
              <img src="/logo_64.png" alt="" className="h-7 w-auto" aria-hidden="true" />
              <h1 className="text-xl font-semibold flex items-center gap-1.5">
                {t("admin_panel_title_base", "Guestivo")}
                {import.meta.env.VITE_APP_ENVIRONMENT === "local" && (
                  <span className="px-1.5 py-0.5 text-[10px] font-bold bg-green-600 text-white rounded-md uppercase tracking-wide">
                    Local
                  </span>
                )}
                {import.meta.env.VITE_APP_ENVIRONMENT === "development" && (
                  <span className="px-1.5 py-0.5 text-[10px] font-bold bg-orange-500 text-white rounded-md uppercase tracking-wide">
                    Dev
                  </span>
                )}
              </h1>
            </NavLink>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Search Filter */}
          {!limitToBilling && (
            <div className="px-3 py-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder={t("admin_nav_search", "Search...")}
                  className="h-9 w-full pl-9 bg-white/60 dark:bg-background/50 border-[hsl(var(--sidebar-border))] focus:bg-white dark:focus:bg-background"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Navigation with collapsible groups */}
          <nav
            className={cn(
              "flex-1 overflow-y-auto px-2 sidebar-scrollbar",
              isCompact ? "py-1" : "py-2",
            )}
          >
            {filteredGroups.map((group) => (
              <CollapsibleNavGroup
                key={group.id}
                group={group}
                isExpanded={expandedGroups.includes(group.id) || !!searchQuery}
                forceExpanded={!!searchQuery}
                onToggle={() => toggleGroup(group.id)}
                userPermissions={userPermissions}
                notificationSources={notificationSources}
                getNotificationCount={getNotificationCount}
                onLinkClick={handleLinkClick}
                isCompact={isCompact}
              />
            ))}
          </nav>

          {/* Settings link at bottom */}
          <nav className="mt-auto px-2 py-2 border-t" style={{ borderColor: 'hsl(var(--sidebar-border))' }}>
            <NavLink
              to="/admin/settings"
              onClick={handleLinkClick}
              className={({ isActive }) =>
                cn(
                  "relative flex items-center gap-2 rounded-md px-2 py-2 text-sm font-medium sidebar-nav-item",
                  "hover:text-foreground",
                  isActive
                    ? "active bg-primary/10 text-primary"
                    : "text-muted-foreground",
                )
              }
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <Settings className="h-4 w-4" />
              </span>
              <span className="flex-1">
                {t("admin_nav_settings", "Settings")}
              </span>
            </NavLink>
          </nav>
        </div>
      </aside>

    </>
  );
};

export default Sidebar;
